/**
 * Minimal SRT parsing/serialization.
 * Supports `<em>...</em>` inline tags which we use for the gold color split.
 */

export interface SrtCue {
  index: number;
  startMs: number;
  endMs: number;
  /** Raw text (may include `<em>...</em>` tags) */
  text: string;
}

export interface CaptionSegment {
  text: string;
  emphasized: boolean;
  /** Explicit per-span color (#RRGGBB) from `<em c="#hex">` — the operator's
   *  Studio "select text → any color". Absent → the emphasized default
   *  (brand accent). Plain `<em>` (no `c`) stays byte-identical. */
  color?: string;
}

/** Matches an `<em>` open tag, with or without a `c="#RRGGBB"` color attr.
 *  Shared so every strip/parse site treats the two forms identically. */
export const EM_OPEN_RE = /<em(?:\s+c="#[0-9a-fA-F]{6}")?>/g;
/** Any em tag — open (plain or coloured) or close — for `.replace()` strips.
 *  Byte-identical to the founding `/<\/?em>/g` on plain markup; the colour
 *  attribute a Studio-baked cue carries is no longer counted as visible
 *  glyphs by the layout budgets (translate.ts / captionLayout.ts). */
export const EM_TAG_RE = /<\/?em(?:\s+c="#[0-9a-fA-F]{6}")?>/g;
/** Strip BOTH `<em>`/`<em c="…">` opens and `</em>` closes (color-aware). */
export function stripEmTags(text: string): string {
  return text.replace(EM_OPEN_RE, "").replace(/<\/em>/g, "");
}
/** True when the text carries any `<em>`/`<em c="…">` span. */
export function hasEmTag(text: string): boolean {
  return /<em(\s|>)/.test(text);
}

export interface DigitSegment {
  text: string;
  isDigit: boolean;
}

/**
 * Split text into runs of digits vs non-digits so the renderer can swap the
 * font on numbers (Arabic-Indic ٠-٩ or ASCII 0-9). Numbers look distinctly
 * better in Lalezar than AlHurra; this lets us mix fonts mid-line.
 *
 * Treats commas/dots/dashes between digits as part of the digit run so a
 * number like "1,548" or "1921-1925" stays one Lalezar span.
 */
export function splitDigits(text: string): DigitSegment[] {
  const DIGIT_RE = /[\d٠-٩۰-۹]/;
  const out: DigitSegment[] = [];
  let buf = "";
  let bufIsDigit: boolean | null = null;
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    let isDigit = DIGIT_RE.test(ch);
    if (!isDigit && /[,.\-]/.test(ch) && i + 1 < text.length) {
      const nextIsDigit = DIGIT_RE.test(text[i + 1]!);
      const prevIsDigit = i > 0 && DIGIT_RE.test(text[i - 1]!);
      if (nextIsDigit && prevIsDigit) isDigit = true;
    }
    if (bufIsDigit === null || bufIsDigit === isDigit) {
      buf += ch;
      bufIsDigit = isDigit;
    } else {
      out.push({ text: buf, isDigit: bufIsDigit });
      buf = ch;
      bufIsDigit = isDigit;
    }
    i++;
  }
  if (buf.length > 0 && bufIsDigit !== null) {
    out.push({ text: buf, isDigit: bufIsDigit });
  }
  return out;
}

const TIMING_RE =
  /(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/;

export function parseSrt(srt: string): SrtCue[] {
  const blocks = srt.replace(/\r\n/g, "\n").trim().split(/\n\s*\n/);
  const cues: SrtCue[] = [];
  for (const block of blocks) {
    const lines = block.split("\n");
    if (lines.length < 2) continue;
    const idx = Number.parseInt(lines[0]!, 10);
    if (Number.isNaN(idx)) continue;
    const m = TIMING_RE.exec(lines[1]!);
    if (!m) continue;
    const startMs = toMs(+m[1]!, +m[2]!, +m[3]!, +m[4]!);
    const endMs = toMs(+m[5]!, +m[6]!, +m[7]!, +m[8]!);
    const text = lines.slice(2).join("\n").trim();
    cues.push({ index: idx, startMs, endMs, text });
  }
  return cues;
}

export function serializeSrt(cues: SrtCue[]): string {
  return cues
    .map(
      (c) =>
        `${c.index}\n${msToTs(c.startMs)} --> ${msToTs(c.endMs)}\n${c.text}`,
    )
    .join("\n\n") + "\n";
}

/**
 * Split a cue's text into segments by `<em>...</em>` tags so the renderer
 * can apply two colors. Tags can be nested-free; whitespace is preserved.
 */
export function splitEmphasis(text: string): CaptionSegment[] {
  // Structural walk rather than a `<em>(.*?)</em>` match: this is the LAST
  // line of defence before pixels. A cue can reach the renderer with
  // malformed markup — a cached SRT written by an older translator, an
  // operator hand-edit — and a pair-matching regex hands the unmatched tags
  // through as segment TEXT, painting a literal «</em>» on screen (the
  // nic-baird clip, 2026-08-24). Here an orphan closer and a redundant
  // opener are simply dropped, and an unclosed span runs to end of line.
  //
  // Openers may carry the Studio's per-span colour, `<em c="#rrggbb">`: the
  // colour rides on every segment of that span; a plain `<em>` parses
  // exactly as before (color undefined).
  const out: CaptionSegment[] = [];
  let buf = "";
  let inEm = false;
  let color: string | undefined;
  const flush = (): void => {
    if (buf.length > 0) {
      out.push({ text: buf, emphasized: inEm, ...(inEm && color ? { color } : {}) });
    }
    buf = "";
  };
  let i = 0;
  while (i < text.length) {
    const open = text.startsWith("<em", i) ? EM_OPEN_AT_START_RE.exec(text.slice(i)) : null;
    if (open) {
      if (!inEm) {
        flush();
        inEm = true;
        color = open[1];
      }
      i += open[0].length;
    } else if (text.startsWith("</em>", i)) {
      if (inEm) {
        flush();
        inEm = false;
        color = undefined;
      }
      i += 5;
    } else {
      buf += text[i]!;
      i++;
    }
  }
  flush();
  return out;
}

/** `<em>` or `<em c="#rrggbb">` at the START of a string → group 1 = colour
 *  (the capturing, anchored sibling of the global `EM_OPEN_RE` above). */
const EM_OPEN_AT_START_RE = /^<em(?:\s+c="(#[0-9a-fA-F]{6})")?>/;

function toMs(h: number, m: number, s: number, ms: number): number {
  return ((h * 60 + m) * 60 + s) * 1000 + ms;
}

/** VTT timestamp pattern: HH:MM:SS.mmm --> HH:MM:SS.mmm (or MM:SS.mmm form) */
const VTT_TIMING_RE =
  /(\d{1,2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{1,2}):(\d{2}):(\d{2})\.(\d{3})/;

/** VTT two-component form: MM:SS.mmm --> MM:SS.mmm */
const VTT_TIMING_SHORT_RE =
  /^(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2})\.(\d{3})/;

/**
 * Parse a WebVTT string (YouTube auto-caption .vtt) into SrtCue[].
 *
 * Differences from SRT handled here:
 *  - "WEBVTT" header line skipped
 *  - Timestamps use "." for ms separator (not ",")
 *  - Optional cue settings after "-->" (stripped)
 *  - Inline per-word tags `<HH:MM:SS.mmm>` and `<c>` stripped
 *  - Rolling duplicate cues de-duped: if consecutive cues share the
 *    same normalized text, only the first is kept
 *
 * Returns SrtCue[] with index = 1-based position after de-dup.
 */
export function parseVtt(vtt: string): SrtCue[] {
  const lines = vtt.replace(/\r\n/g, "\n").split("\n");
  const blocks: string[][] = [];
  let current: string[] = [];

  for (const line of lines) {
    if (line.trim() === "") {
      if (current.length > 0) { blocks.push(current); current = []; }
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) blocks.push(current);

  const raw: SrtCue[] = [];
  let cueNum = 0;

  for (const block of blocks) {
    // Skip the WEBVTT header block
    if (block[0]?.trim().startsWith("WEBVTT")) continue;

    // Find the timing line (the one containing "-->")
    const timingIdx = block.findIndex((l) => l.includes("-->"));
    if (timingIdx < 0) continue;

    const timingLine = block[timingIdx]!;
    // Strip cue settings after the timing (e.g. "align:start position:0%")
    const timingOnly = timingLine.split(/\s+(?:align|position|line|size|vertical):/)[0]!.trim();

    let startMs: number;
    let endMs: number;
    const m3 = VTT_TIMING_RE.exec(timingOnly);
    const m2 = m3 ? null : VTT_TIMING_SHORT_RE.exec(timingOnly);
    if (m3) {
      startMs = toMs(+m3[1]!, +m3[2]!, +m3[3]!, +m3[4]!);
      endMs = toMs(+m3[5]!, +m3[6]!, +m3[7]!, +m3[8]!);
    } else if (m2) {
      startMs = toMs(0, +m2[1]!, +m2[2]!, +m2[3]!);
      endMs = toMs(0, +m2[4]!, +m2[5]!, +m2[6]!);
    } else {
      continue;
    }

    // Text is everything after the timing line
    const rawText = block.slice(timingIdx + 1).join(" ").trim();
    // Strip inline timestamp tags <HH:MM:SS.mmm> and <c>/<\/c>
    const text = rawText
      .replace(/<\d{1,2}:\d{2}:\d{2}\.\d{3}>/g, "")
      .replace(/<[/]?c>/g, "")
      .replace(/<[/]?[a-z][^>]*>/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    if (!text) continue;
    raw.push({ index: 0, startMs, endMs, text });
  }

  // De-duplicate consecutive cues with identical text (rolling VTT pattern)
  const deduped: SrtCue[] = [];
  let lastText = "";
  for (const cue of raw) {
    if (cue.text === lastText) continue;
    lastText = cue.text;
    cueNum++;
    deduped.push({ ...cue, index: cueNum });
  }
  return deduped;
}

function msToTs(ms: number): string {
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const milli = ms % 1000;
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)},${pad(milli, 3)}`;
}

function pad(n: number, width: number): string {
  return n.toString().padStart(width, "0");
}

/**
 * Frame-0 caption: return cues with the FIRST cue's start extended to 0ms so
 * the branded caption is visible on the very first frame — but ONLY when it
 * already starts within maxMs of the top (a genuinely late first cue is a
 * deliberate cold open and is left alone). Non-mutating; ordering assumed
 * (SRT cues are time-ordered). See CAPTION_FROM_FRAME0 in lib/config.ts.
 */
export function extendFirstCueToFrameZero(cues: SrtCue[], maxMs: number): SrtCue[] {
  const first = cues[0];
  if (!first || first.startMs <= 0 || first.startMs > maxMs) return cues;
  return [{ ...first, startMs: 0 }, ...cues.slice(1)];
}
